/* eslint-disable @typescript-eslint/no-explicit-any */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Offcanvas } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';
import Button from 'components/base/Button';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import SupperModal from 'components/modals/SupperModal';
import CreateDebitCreditSettlementPlan from './CreateDebitCreditSettlementPlan';
import usersService from 'service/users';
import debitCreditSettlementPlanService from 'service/debitCreditSettlementPlanService';
import i18n from 'i18next';

export const DebitCreditSettlementPlanBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'debit_credit_settlement_plan',
    active: true
  }
];

export type OrderPlanType = {
  type: string;
  status_pid: number;
  currency: string;
  amount: number;
  debitor_pid: number;
  charger_pid?: number;
  date_create: string;
  guid: number;
  photo: string;
  comment: string;
  date_from: number;
  date_to: number;
};

const defaultColors: Record<number, { bg: string; text: string }> = {
  1: { bg: '#e0f7fa', text: '#00796b' },
  2: { bg: '#fff3e0', text: '#ef6c00' },
  3: { bg: '#fce4ec', text: '#c2185b' },
  4: { bg: '#e8f5e9', text: '#2e7d32' },
  5: { bg: '#ede7f6', text: '#5e35b1' },
  6: { bg: '#ffebee', text: '#d32f2f' },
  7: { bg: '#fffde7', text: '#fbc02d' },
  8: { bg: '#f3e5f5', text: '#8e24aa' },
  9: { bg: '#e0f2f1', text: '#004d40' }
};
const statusCodes = [
  'CREATED_BY_SUPPLIER',
  'CREATED_BY_CHARGER',
  'SUBMITTED_BY_CHARGER',
  'CANCELLED_BY_CHARGER',
  'CONFIRMED_BY_RECIPIENT',
  'REJECTED_BY_RECIPIENT',
  'CONFIRMED_BY_SUPPLIER',
  'REJECTED_BY_SUPPLIER',
  'COMPLETED'
];

const DebitCreditSettlementPlan = () => {
  const { t } = useTranslation();

  const [openOrderPlan, setOpenOrderPlan] = useState(false);
  const [selectedOrderPlanId, setSelectedOrderPlanId] = useState<number>(0);
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedOrderPlanToDelete, setSelectedOrderPlanToDelete] = useState<
    number | null
  >(null);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [chargers, setChargers] = useState<{ pid: number; name: string }[]>([]);
  const [debitors, setDebitors] = useState<{ pid: number; name: string }[]>([]);

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_ORDER_PLAN', currentPage],
    () => {
      const params = {
        limit: 10,
        offset: pageToOffset(currentPage, 10),
        type: 'PLANNED',
        recipient_pid: 490
      };
      return debitCreditSettlementPlanService
        .getList(params)
        .then((res: any) => res);
    },
    { enabled: !!currentPage }
  );
  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));

      const plans = data?.debit_credit_settlement_plans || [];

      const debitorMap = new Map<number, string>();

      plans.forEach((plan: any) => {
        const debitor = plan?.debitor;
        if (debitor && debitor.pid && debitor.name) {
          debitorMap.set(debitor.pid, debitor.name);
        }
      });

      const debitorList = Array.from(debitorMap.entries()).map(
        ([pid, name]) => ({
          pid,
          name
        })
      );
      setDebitors(debitorList);
    }
  }, [data]);

  useEffect(() => {
    usersService.getList({}).then((res: any) => {
      const list = res?.data?.users || [];
      setChargers(
        list.map((user: any) => ({
          pid: Number(user.id_number),
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
        }))
      );
    });
  }, []);

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const orderPlanData: OrderPlanType[] = useMemo(() => {
    return (
      data?.debit_credit_settlement_plans?.map((item: any) => ({
        type: t(item?.type) || 'PLANNED',
        status_pid: item?.status?.pid || 0,
        currency: item?.currency?.name?.[i18n.language] || '-',
        amount: item?.amount,
        debitor_pid: item?.debitor_pid,
        charger_pid: item?.charger_pid,
        date_create: new Date(item?.created_at).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),

        guid: item?.pid,
        photo: item?.foto,
        comment: item?.comment,
        date_from: item?.date_from
          ? new Date(item?.date_from).toLocaleDateString('ru-RU')
          : '',
        date_to: item?.date_to
          ? new Date(item?.date_to).toLocaleDateString('ru-RU')
          : ''
      })) ?? []
    );
  }, [data]);

  const handleDelete = (pid: number) => {
    setSelectedOrderPlanToDelete(pid);
    setOpenSupperModal(true);
  };

  const columns: ColumnDef<OrderPlanType>[] = [
    {
      accessorKey: 'photo',
      header: t('photo'),
      cell: info => {
        const photoUrl = info.getValue() as string;
        return photoUrl ? (
          <img
            src={photoUrl}
            alt="photo"
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
        ) : (
          '-'
        );
      },
      meta: { cellProps: { className: 'text-center' } }
    },
    {
      accessorKey: 'comment',
      header: t('comment'),
      cell: info => (
        <div
          style={{
            maxWidth: '200px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {info.getValue() as string}
        </div>
      )
    },
    { accessorKey: 'type', header: t('type') },
    {
      accessorKey: 'date_from',
      header: t('date_from')
    },
    {
      accessorKey: 'date_to',
      header: t('date_to')
    },
    {
      accessorKey: 'amount',
      header: t('amount'),
      cell: info => {
        const value = info.getValue() as number;
        return value?.toLocaleString('ru-RU') ?? '-';
      }
    },
    { accessorKey: 'currency', header: t('currency') },
    {
      accessorKey: 'debitor_pid',
      header: t('debitor'),
      cell: info => {
        const pid = info.getValue() as number;
        const debitor = debitors.find(d => d.pid === pid);
        return debitor?.name || pid || '-';
      }
    },
    {
      accessorKey: 'charger_pid',
      header: t('charger'),
      cell: info => {
        const pid = info.getValue() as number;
        const charger = chargers.find(user => user.pid === pid);
        return charger?.name || pid || '-';
      }
    },
    { accessorKey: 'date_create', header: t('date_create') },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const status = rowData?.row?.original?.status_pid;
        const label = t(statusCodes[status - 1]) || status;
        const backgroundColor = defaultColors[status]?.bg || '#ffffff';
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex align-items-center">
            <p
              style={{
                background: backgroundColor,
                padding: '4px 8px',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: 0,
                whiteSpace: 'nowrap'
              }}
            >
              {label}
            </p>
          </div>
        );
      }
    },
    {
      id: 'action',
      cell: rowData => (
        <div className="d-flex gap-2">
          <Button
            variant="hover"
            className="p-2"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedOrderPlanId(rowData?.row?.original?.guid);
              setOpenOrderPlan(true);
            }}
          >
            <FeatherIcon icon="edit-2" className="cursor-pointer" size={20} />
          </Button>
          <Button
            variant="hover"
            className="p-2"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete(rowData?.row?.original?.guid);
            }}
          >
            <FeatherIcon
              icon="trash-2"
              className="cursor-pointer text-danger"
              size={20}
            />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: orderPlanData,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (selectedOrderPlanToDelete && confirmToDelete) {
      debitCreditSettlementPlanService
        .delete(selectedOrderPlanToDelete)
        .finally(() => {
          refetch();
          setSelectedOrderPlanId(0);
          setOpenSupperModal(false);
          setConfirmToDelete(false);
          setSelectedOrderPlanToDelete(null);
        });
    }
  }, [confirmToDelete, selectedOrderPlanToDelete, refetch]);

  return (
    <div>
      <PageBreadcrumb items={DebitCreditSettlementPlanBreadcrumbItems} />

      <div className="d-flex justify-content-between">
        <h2>{t('debit_credit_settlement_plan')}</h2>
        <Button
          className="d-flex align-items-center gap-2"
          style={{ height: '48px' }}
          variant="primary"
          onClick={() => setOpenOrderPlan(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t('new')}
        </Button>
      </div>

      <AdvanceTableProvider {...table}>
        <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('debit_credit_settlement_plan')}
        bodyText={t('you_want_delete')}
      />

      <Offcanvas
        show={openOrderPlan}
        onHide={() => {
          setOpenOrderPlan(false);
          setSelectedOrderPlanId(0);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t(selectedOrderPlanId ? 'edit' : 'new')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <CreateDebitCreditSettlementPlan
            setOpenOrderPlan={setOpenOrderPlan}
            selectedOrderPlanId={selectedOrderPlanId}
            refetch={refetch}
            setSelectedOrderPlanId={setSelectedOrderPlanId}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default DebitCreditSettlementPlan;
